import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import type {
  ChangeEvent,
  DragEvent,
  FormEvent,
  KeyboardEvent,
} from 'react';
import {
  MAX_UPLOAD_BYTES,
  createSchedule,
  schedulesQueryKey,
  sendMessage,
  uploadFile,
} from '../lib/api';
import { describeCadence, scheduleConfirmSentence, SCHEDULE_NAME_MAX } from '../lib/schedule';
import { useDraft } from '../hooks/useDraft';
import { FilePreview, type StagedFile, type StagedFileState } from './FilePreview';
import { SendScheduleMenu } from './SendScheduleMenu';

export interface ReplyContext {
  messageId: string;
  senderPersona: string;
  preview: string;
}

interface ComposerProps {
  roomId: string;
  disabled?: boolean;
  disabledReason?: string;
  replyTo?: ReplyContext | null;
  onCancelReply?: () => void;
  /** Override the textarea placeholder (e.g. a DM's "Message <peer>…"). Defaults to the
   *  full send-hint used in shared spaces. */
  placeholder?: string;
  /** Which surface this composer serves. `'dm'` DROPS the Send ▾ / schedule menu entirely:
   *  scheduling is an org-space act, not a 1:1 DM act (Erick ruling, Amendment 8 #3, T-0224).
   *  Mirrors MessageThread's `variant`; DMView passes `'dm'`, spaces default to `'space'`. */
  variant?: 'space' | 'dm';
}

// Placeholder NAMES the room (recognition, not recall) — the mechanics live in
// the persistent hint line below, so they don't vanish on the first keystroke and
// carry no ALL-CAPS "AS" (T-0200). Callers pass a room-specific "Message <name>…";
// this is the neutral fallback.
const DEFAULT_PLACEHOLDER = 'Write a message…';

/** The quiet, persistent mechanics hint — replaces the paragraph-in-a-placeholder. */
const COMPOSER_HINT = 'markdown · Enter sends · drop files anywhere';

/** The submit button says what pressing it will do — the label is the last honest
 *  chance to notice you armed a repeat rule you didn't mean to. */
function sendLabel(scheduling: boolean, sending: boolean, arming: boolean): string {
  if (arming) return 'Scheduling…';
  if (sending) return 'Sending…';
  return scheduling ? 'Schedule' : 'Send';
}

export function Composer({
  roomId,
  disabled = false,
  disabledReason,
  replyTo,
  onCancelReply,
  placeholder = DEFAULT_PLACEHOLDER,
  variant = 'space',
}: ComposerProps) {
  // Scheduling is an org-space act only — a DM composer never offers the Send ▾ (T-0224).
  // With no ▾ there is no way to set a pending rule, so the whole schedule path (the
  // confirm row, `Schedule` label, arm mutation) is unreachable in a DM by construction.
  const schedulable = variant === 'space';
  const fileInputId = useId();
  // Draft text is persisted per room, so switching spaces (or a reload) keeps
  // an unsent message. `content`/`setContent` keep their names via aliasing.
  const { draft: content, setDraft: setContent, clearDraft } = useDraft(roomId);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [draggingOver, setDraggingOver] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  // Single polite live region narrating the send lifecycle for screen-reader
  // users — sighted users track it via the button label and the inline error
  // row, AT users via this. One channel avoids assertive/polite double-speak.
  const [sendAnnouncement, setSendAnnouncement] = useState('');
  // A repeat rule chosen from ▾ but not yet armed. While it is set, the composer IS
  // the confirm: it says the rule back in words, and Send becomes Schedule. That's
  // the whole "+2 taps over a one-off" budget — ▾, the cadence, then the button the
  // user was already reaching for.
  const [pendingRule, setPendingRule] = useState<string | null>(null);
  // The optional "name this schedule" title (packet 004 ask #3). Blank ⇒ the row falls
  // back to the first words of the Request. Only meaningful while a rule is pending.
  const [pendingName, setPendingName] = useState('');
  const queryClient = useQueryClient();
  const abortControllers = useRef<Map<string, AbortController>>(new Map());

  const mutation = useMutation({
    mutationFn: (body: string) =>
      sendMessage(roomId, {
        content: body,
        parent_id: replyTo?.messageId,
      }),
    onMutate: () => setSendAnnouncement('Sending message…'),
    onSuccess: () => {
      clearDraft();
      onCancelReply?.();
      queryClient.invalidateQueries({ queryKey: ['space', roomId, 'messages'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'spaces'] });
      setSendAnnouncement('Message sent.');
    },
    onError: () =>
      setSendAnnouncement('Message failed to send. Press Retry to try again.'),
  });

  const scheduleMutation = useMutation({
    mutationFn: ({ body, rule, name }: { body: string; rule: string; name: string }) =>
      createSchedule(roomId, {
        request_body: body,
        repeat_rule: rule,
        // Blank ⇒ omit the field, so the row keeps its first-words title. (The server
        // also treats blank as absent; not sending it keeps the wire's intent clean.)
        ...(name.trim() ? { name: name.trim() } : {}),
      }),
    onMutate: () => setSendAnnouncement('Arming your Schedule…'),
    onSuccess: (schedule) => {
      clearDraft();
      onCancelReply?.();
      setPendingRule(null);
      setPendingName('');
      // The space sheet's row is the receipt for this action; it must be there the
      // moment the user goes looking, not one refetch later.
      queryClient.invalidateQueries({ queryKey: schedulesQueryKey(roomId) });
      setSendAnnouncement(
        `Scheduled. ${describeCadence(schedule.repeat_rule)}. See it in the space sheet.`,
      );
    },
    onError: () => setSendAnnouncement('Could not arm the Schedule. Press Schedule to try again.'),
  });

  const isDisabled = disabled || mutation.isPending || scheduleMutation.isPending;
  const scheduling = pendingRule !== null;

  function setStagedState(id: string, state: StagedFileState) {
    setStaged((prev) => prev.map((s) => (s.id === id ? { ...s, state } : s)));
  }

  async function startUpload(file: File) {
    if (file.size > MAX_UPLOAD_BYTES) {
      setGlobalError(`${file.name} exceeds 50MB cap`);
      return;
    }
    const id = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setStaged((prev) => [...prev, { id, file, state: { kind: 'pending' } }]);

    const controller = new AbortController();
    abortControllers.current.set(id, controller);

    setStagedState(id, { kind: 'uploading', progress: { loaded: 0, total: file.size } });
    try {
      await uploadFile(
        roomId,
        file,
        (progress) => setStagedState(id, { kind: 'uploading', progress }),
        controller.signal,
      );
      setStagedState(id, { kind: 'done', url: '' });
      queryClient.invalidateQueries({ queryKey: ['space', roomId, 'messages'] });
      setTimeout(() => {
        abortControllers.current.delete(id);
        setStaged((prev) => prev.filter((s) => s.id !== id));
      }, 800);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Upload failed';
      setStagedState(id, { kind: 'error', message });
    }
  }

  function handleFiles(files: FileList | null) {
    if (isDisabled || !files) return;
    setGlobalError(null);
    Array.from(files).forEach(startUpload);
  }

  function handleCancel(id: string) {
    abortControllers.current.get(id)?.abort();
    abortControllers.current.delete(id);
    setStaged((prev) => prev.filter((s) => s.id !== id));
  }

  // The one commit path: with a rule pending it arms a Schedule, otherwise it sends
  // the Request once. Enter, the Send button, and "Send now" all land here, so the
  // gesture never disagrees with the button's label.
  function commit() {
    if (isDisabled || !content.trim()) return;
    if (pendingRule) {
      scheduleMutation.mutate({ body: content, rule: pendingRule, name: pendingName });
      return;
    }
    mutation.mutate(content);
  }

  function sendNow() {
    setPendingRule(null);
    setPendingName('');
    if (isDisabled || !content.trim()) return;
    mutation.mutate(content);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    commit();
  }

  // The failed message's text is preserved in the draft (clearDraft only runs
  // on success), so retrying just re-sends the current composer content.
  function handleRetry() {
    if (isDisabled || !content.trim()) return;
    mutation.mutate(content);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commit();
    }
  }

  function handleDragOver(e: DragEvent<HTMLFormElement>) {
    e.preventDefault();
    if (isDisabled) return;
    setDraggingOver(true);
  }

  function handleDragLeave(e: DragEvent<HTMLFormElement>) {
    if (e.target === e.currentTarget) setDraggingOver(false);
  }

  function handleDrop(e: DragEvent<HTMLFormElement>) {
    e.preventDefault();
    setDraggingOver(false);
    if (isDisabled) return;
    handleFiles(e.dataTransfer.files);
  }

  return (
    <form
      className={`composer ${draggingOver ? 'composer-drag-over' : ''}`}
      onSubmit={handleSubmit}
      aria-label="Send message"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {replyTo && (
        <div className="composer-reply-banner" role="status">
          <span className="composer-reply-label">
            Replying to <strong>{replyTo.senderPersona}</strong>:{' '}
            {replyTo.preview.slice(0, 80)}
            {replyTo.preview.length > 80 ? '…' : ''}
          </span>
          <button
            type="button"
            className="composer-reply-cancel"
            onClick={onCancelReply}
            aria-label="Cancel reply"
          >
            ✕
          </button>
        </div>
      )}
      {staged.length > 0 && (
        <ul className="composer-staged" aria-label="Staged uploads">
          {staged.map((s) => (
            <li key={s.id}>
              <FilePreview staged={s} onCancel={handleCancel} />
            </li>
          ))}
        </ul>
      )}
      {globalError && (
        <p className="composer-global-error" role="alert">
          {globalError}
        </p>
      )}
      <textarea
        className="composer-input"
        value={content}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={3}
        disabled={isDisabled}
        aria-label="Message content"
      />
      {pendingRule !== null && (
        // Words, never cron. The rule is a UTC string on the wire; what the user
        // reads is their own clock, what it costs, and when it first runs.
        <div className="composer-schedule">
          <div className="composer-schedule-confirm" role="status">
            <span className="composer-schedule-glyph" aria-hidden="true">
              ↻
            </span>
            <span className="composer-schedule-sentence">
              {scheduleConfirmSentence(pendingRule)}
            </span>
            <button
              type="button"
              className="composer-schedule-cancel"
              onClick={() => {
                setPendingRule(null);
                setPendingName('');
              }}
              aria-label="Cancel schedule"
            >
              ✕
            </button>
          </div>
          {/* Optional short title (packet 004 ask #3). Left blank, the row falls back
              to the first words of the Request — so this never becomes a required step. */}
          <input
            type="text"
            className="composer-schedule-name"
            value={pendingName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPendingName(e.target.value)}
            placeholder="Name this schedule (optional)"
            aria-label="Name this schedule (optional)"
            maxLength={SCHEDULE_NAME_MAX}
            disabled={isDisabled}
          />
        </div>
      )}
      <div className="composer-actions">
        {!disabled && (
          <span className="composer-hint" aria-hidden="true">
            {COMPOSER_HINT}
          </span>
        )}
        {mutation.isError && (
          <div className="composer-error-row">
            <span className="composer-error">Couldn't send your message.</span>
            <button
              type="button"
              className="composer-retry btn-secondary"
              onClick={handleRetry}
              disabled={isDisabled || !content.trim()}
            >
              Retry
            </button>
          </div>
        )}
        {scheduleMutation.isError && (
          <div className="composer-error-row">
            <span className="composer-error">Couldn't arm your Schedule.</span>
          </div>
        )}
        <input
          id={fileInputId}
          type="file"
          multiple
          className="composer-file-input"
          disabled={isDisabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
          aria-label="Attach files"
        />
        <label
          htmlFor={fileInputId}
          className="composer-attach"
          aria-disabled={isDisabled}
        >
          Attach
        </label>
        {schedulable ? (
          <SendScheduleMenu
            // A Schedule posts a NEW Request at each run — the schedules route carries
            // no `parent_id`. Arming one from a reply would silently drop the thread,
            // so the ▾ is unavailable while a reply is staged rather than quietly
            // lying about where the 8:00 message will land.
            disabled={isDisabled || !content.trim() || Boolean(replyTo)}
            disabledReason={
              replyTo ? 'A Schedule posts a new Request, not a reply.' : undefined
            }
            onSendNow={sendNow}
            onPickRule={setPendingRule}
          >
            <button
              type="submit"
              disabled={isDisabled || !content.trim()}
              className="composer-send"
            >
              {sendLabel(scheduling, mutation.isPending, scheduleMutation.isPending)}
            </button>
          </SendScheduleMenu>
        ) : (
          // DM: a plain Send, no ▾ (T-0224).
          <button
            type="submit"
            disabled={isDisabled || !content.trim()}
            className="composer-send"
          >
            {sendLabel(scheduling, mutation.isPending, scheduleMutation.isPending)}
          </button>
        )}
      </div>
      {disabled && (
        <p className="composer-disabled" role="status">
          {disabledReason ?? 'Messaging is disabled for this space.'}
        </p>
      )}
      <p
        className="composer-live"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {sendAnnouncement}
      </p>
    </form>
  );
}
